import { Box, Container, Typography, Button, Grid } from "@mui/material";
import { Link } from "react-router-dom";
import { useDocumentTitle } from "../../utils/data";
import { keyframes } from "@mui/system";
import JobOpportunities from "../../components/JobOpportunities";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
`;

const floatBall = keyframes`
  0%, 100% { transform: translate(0, 0); }
  25% { transform: translate(20px, -20px); }
  50% { transform: translate(-10px, 20px); }
  75% { transform: translate(10px, -10px); }
`;

const scaleInCenter = keyframes`
  from { opacity: 0; transform: scale(0.85); }
  to { opacity: 1; transform: scale(1); }
`;

const bounce = keyframes`
  0%, 100% { transform: scale(1); }
  25% { transform: scale(1.2) rotate(-5deg); }
  50% { transform: scale(1.1) rotate(5deg); }
  75% { transform: scale(1.15) rotate(-3deg); }
`;

const benefitsData = [
  {
    icon: "💰",
    title: "Competitive Salary",
    description: "Industry-leading compensation packages with performance bonuses",
  },
  {
    icon: "🏥",
    title: "Health Benefits",
    description: "Comprehensive health, dental, and vision insurance coverage",
  },
  {
    icon: "📚",
    title: "Learning & Growth",
    description: "Continuous learning opportunities and professional development",
  },
  {
    icon: "🏠",
    title: "Flexible Work",
    description: "Remote-friendly culture with flexible working hours",
  },
  {
    icon: "🌴",
    title: "Paid Time Off",
    description: "Generous vacation policy and paid holidays",
  },
  {
    icon: "🎯",
    title: "Career Growth",
    description: "Clear career paths with mentorship and advancement opportunities",
  },
];

const valuesData = [
  {
    icon: "🚀",
    title: "Innovation",
    description: "We embrace new ideas and cutting-edge technologies",
  },
  {
    icon: "🤝",
    title: "Collaboration",
    description: "We work together to achieve exceptional results",
  },
  {
    icon: "⭐",
    title: "Excellence",
    description: "We strive for the highest quality in everything we do",
  },
  {
    icon: "💡",
    title: "Creativity",
    description: "We encourage creative thinking and problem-solving",
  },
];

const Careers = () => {
  useDocumentTitle("Briozon - Careers");

  return (
    <Box>
      {/* Hero Banner */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #f8fafc 0%, #f0f4ff 100%)",
          py: { xs: 6, sm: 8, md: 10 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Floating decorative elements */}
        <Box
          sx={{
            position: "absolute",
            top: "10%",
            right: "5%",
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(91,124,250,0.1) 0%, rgba(118,75,162,0.1) 100%)",
            animation: `${floatBall} 8s ease-in-out infinite`,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "15%",
            left: "3%",
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(118,75,162,0.1) 0%, rgba(91,124,250,0.1) 100%)",
            animation: `${floatBall} 6s ease-in-out infinite 1s`,
          }}
        />

        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, sm: 6, md: 8 }} alignItems="center">
            {/* Left Content */}
            <Grid item xs={12} md={6}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    fontSize: { xs: "12px", sm: "14px" },
                    fontWeight: 700,
                    color: "#5b7cfa",
                    letterSpacing: "2px",
                    mb: 1,
                    display: "block",
                    animation: `${fadeUp} 0.6s ease-out 0.05s both`,
                  }}
                >
                  JOIN OUR TEAM
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: "32px", sm: "42px", md: "48px" },
                    fontWeight: 800,
                    color: "#1b1f33",
                    lineHeight: 1.2,
                    mb: 2,
                    animation: `${fadeUp} 0.6s ease-out 0.1s both`,
                  }}
                >
                  Careers at{" "}
                  <Box
                    component="span"
                    sx={{
                      background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    Briozon
                  </Box>
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: "16px", sm: "19px", md: "20px" },
                    color: "#4c5472",
                    lineHeight: 1.8,
                    mb: 3,
                    animation: `${fadeUp} 0.6s ease-out 0.2s both`,
                  }}
                >
                  Join Briozon and drive innovation in software development! Grow in a 
                  collaborative environment where your impact matters.
                </Typography>
                <Button
                  onClick={() => {
                    document.getElementById('job-opportunities')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  variant="contained"
                  sx={{
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    color: "#fff",
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    borderRadius: "12px",
                    fontSize: "16px",
                    textTransform: "none",
                    boxShadow: "0 8px 24px rgba(91, 124, 250, 0.3)",
                    animation: `${fadeUp} 0.6s ease-out 0.3s both`,
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 12px 32px rgba(91, 124, 250, 0.4)",
                    },
                  }}
                >
                  View Open Positions
                </Button>
              </Box>
            </Grid>

            {/* Right - Creative Visual */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: "relative",
                  height: { xs: 320, sm: 400, md: 450 },
                  animation: `${scaleInCenter} 0.8s ease-out 0.2s both`,
                }}
              >
                {/* Main gradient circle */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 220, sm: 280, md: 320 },
                    height: { xs: 220, sm: 280, md: 320 },
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, rgba(91,124,250,0.15) 0%, rgba(118,75,162,0.15) 100%)",
                    filter: "blur(40px)",
                  }}
                />

                {/* Central icon */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 100, sm: 120 },
                    height: { xs: 100, sm: 120 },
                    borderRadius: "24px",
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: { xs: "48px", sm: "56px" },
                    boxShadow: "0 20px 40px rgba(91, 124, 250, 0.4)",
                    animation: `${floatBall} 6s ease-in-out infinite`,
                    zIndex: 2,
                  }}
                >
                  🎯
                </Box>

                {/* Floating icons */}
                <Box
                  sx={{
                    position: "absolute",
                    top: { xs: "5%", sm: "8%" },
                    left: { xs: "5%", sm: "10%" },
                    width: 56,
                    height: 56,
                    borderRadius: "16px",
                    background: "rgba(255,255,255,0.95)",
                    boxShadow: "0 8px 32px rgba(17, 24, 39, 0.1)",
                    backdropFilter: "blur(10px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "28px",
                    animation: `${floatBall} 5s ease-in-out infinite 0.5s`,
                    zIndex: 1,
                  }}
                >
                  💼
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    top: { xs: "5%", sm: "5%" },
                    right: { xs: "5%", sm: "8%" },
                    width: 56,
                    height: 56,
                    borderRadius: "16px",
                    background: "rgba(255,255,255,0.95)",
                    boxShadow: "0 8px 32px rgba(17, 24, 39, 0.1)",
                    backdropFilter: "blur(10px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "28px",
                    animation: `${floatBall} 6s ease-in-out infinite 1s`,
                    zIndex: 1,
                  }}
                >
                  🚀
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    bottom: { xs: "8%", sm: "10%" },
                    left: { xs: "8%", sm: "15%" },
                    width: 56,
                    height: 56,
                    borderRadius: "16px",
                    background: "rgba(255,255,255,0.95)",
                    boxShadow: "0 8px 32px rgba(17, 24, 39, 0.1)",
                    backdropFilter: "blur(10px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "28px",
                    animation: `${floatBall} 7s ease-in-out infinite 1.5s`,
                    zIndex: 1,
                  }}
                >
                  💡
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    bottom: { xs: "5%", sm: "8%" },
                    right: { xs: "5%", sm: "10%" },
                    width: 56,
                    height: 56,
                    borderRadius: "16px",
                    background: "rgba(255,255,255,0.95)",
                    boxShadow: "0 8px 32px rgba(17, 24, 39, 0.1)",
                    backdropFilter: "blur(10px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "28px",
                    animation: `${floatBall} 5.5s ease-in-out infinite 2s`,
                    zIndex: 1,
                  }}
                >
                  ⭐
                </Box>

                {/* Additional floating icons */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "60%",
                    left: { xs: "15%", sm: "20%" },
                    width: 44,
                    height: 44,
                    borderRadius: "12px",
                    background: "linear-gradient(135deg, rgba(91,124,250,0.2) 0%, rgba(118,75,162,0.2) 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "20px",
                    animation: `${floatBall} 6s ease-in-out infinite 0.3s`,
                  }}
                >
                  🤝
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    top: "62%",
                    right: { xs: "15%", sm: "18%" },
                    width: 44,
                    height: 44,
                    borderRadius: "12px",
                    background: "linear-gradient(135deg, rgba(118,75,162,0.2) 0%, rgba(91,124,250,0.2) 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "20px",
                    animation: `${floatBall} 4.5s ease-in-out infinite 1.8s`,
                  }}
                >
                  📈
                </Box>

                {/* Decorative rings */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 200, sm: 260 },
                    height: { xs: 200, sm: 260 },
                    borderRadius: "50%",
                    border: "2px dashed rgba(91, 124, 250, 0.2)",
                    animation: `${floatBall} 10s ease-in-out infinite`,
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 280, sm: 360 },
                    height: { xs: 280, sm: 360 },
                    borderRadius: "50%",
                    border: "1px dashed rgba(118, 75, 162, 0.15)",
                    animation: `${floatBall} 12s ease-in-out infinite reverse`,
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Our Values Section */}
      <Box sx={{ py: { xs: 8, sm: 10, md: 12 }, background: "linear-gradient(135deg, #f6f8ff 0%, #eef3ff 100%)" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: { xs: 5, sm: 6 } }}>
            <Typography
              variant="overline"
              sx={{
                fontSize: { xs: "12px", sm: "14px" },
                fontWeight: 700,
                color: "#5b7cfa",
                letterSpacing: "2px",
                mb: 1,
                display: "block",
                animation: `${fadeUp} 0.6s ease-out both`,
              }}
            >
              OUR CULTURE
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "28px", sm: "36px", md: "42px" },
                fontWeight: 800,
                color: "#1b1f33",
                animation: `${fadeUp} 0.6s ease-out 0.05s both`,
              }}
            >
              What We{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Value
              </Box>
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 3, sm: 4 }}>
            {valuesData.map((item, idx) => (
              <Grid item xs={12} sm={6} md={3} key={item.title}>
                <Box
                  sx={{
                    p: { xs: 3, sm: 4 },
                    background: "#fff",
                    borderRadius: "20px",
                    border: "1px solid rgba(91, 124, 250, 0.15)",
                    textAlign: "center",
                    transition: "all 0.4s ease",
                    animation: `${fadeUp} 0.6s ease-out ${0.1 * idx}s both`,
                    position: "relative",
                    zIndex: 1,
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: "0 20px 48px rgba(91, 124, 250, 0.15)",
                      border: "1px solid rgba(91, 124, 250, 0.3)",
                      zIndex: 2,
                      "& .value-icon": {
                        animation: `${bounce} 0.6s ease-in-out`,
                        background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                      },
                    },
                  }}
                >
                  <Box
                    className="value-icon"
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: "18px",
                      background: "linear-gradient(135deg, rgba(91,124,250,0.12) 0%, rgba(118,75,162,0.12) 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "36px",
                      mx: "auto",
                      mb: 2.5,
                      transition: "all 0.4s ease",
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: { xs: "20px", sm: "22px" },
                      fontWeight: 700,
                      color: "#1f2937",
                      mb: 1.5,
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: "16px", sm: "17px" },
                      color: "#4b5563",
                      lineHeight: 1.7,
                    }}
                  >
                    {item.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Resume CTA Section */}
      <Box sx={{ py: { xs: 8, sm: 10, md: 12 }, background: "#fff" }}>
        <Container maxWidth="md">
          <Box
            sx={{
              textAlign: "center",
              px: { xs: 3, sm: 6 },
              py: { xs: 5, sm: 7 },
              borderRadius: "20px",
              background: "linear-gradient(135deg, #f6f8ff 0%, #eef3ff 100%)",
              border: "1px solid rgba(91, 124, 250, 0.18)",
              animation: `${fadeUp} 0.6s ease-out both`,
            }}
          >
            <Typography
              variant="overline"
              sx={{
                fontSize: { xs: "12px", sm: "14px" },
                fontWeight: 700,
                color: "#5b7cfa",
                letterSpacing: "2px",
                mb: 1,
                display: "block",
              }}
            >
              JOIN OUR TEAM
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "26px", sm: "34px", md: "38px" },
                fontWeight: 800,
                color: "#1b1f33",
                mb: 2,
              }}
            >
              Share Your{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Resume
              </Box>
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "16px", sm: "18px" },
                color: "#4b5563",
                lineHeight: 1.7,
                mb: 3,
              }}
            >
              Send your resume to{" "}
              <Box component="span" sx={{ fontWeight: 700, color: "#1b1f33" }}>
                hr@briozon.com
              </Box>{" "}
              to join our team.
            </Typography>
            <Button
              component="a"
              href="mailto:hr@briozon.com"
              variant="contained"
              sx={{
                background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                color: "#fff",
                fontWeight: 700,
                px: 4,
                py: 1.5,
                borderRadius: "12px",
                fontSize: "16px",
                textTransform: "none",
                boxShadow: "0 8px 24px rgba(91, 124, 250, 0.3)",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 12px 32px rgba(91, 124, 250, 0.4)",
                },
              }}
            >
              Email Your Resume
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box sx={{ py: { xs: 8, sm: 10, md: 12 }, background: "#fff" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: { xs: 5, sm: 6 } }}>
            <Typography
              variant="overline"
              sx={{
                fontSize: { xs: "12px", sm: "14px" },
                fontWeight: 700,
                color: "#5b7cfa",
                letterSpacing: "2px",
                mb: 1,
                display: "block",
                animation: `${fadeUp} 0.6s ease-out both`,
              }}
            >
              WHY JOIN US
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "28px", sm: "36px", md: "42px" },
                fontWeight: 800,
                color: "#1b1f33",
                animation: `${fadeUp} 0.6s ease-out 0.05s both`,
              }}
            >
              Benefits &{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Perks
              </Box>
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 3, sm: 4 }}>
            {benefitsData.map((item, idx) => (
              <Grid item xs={12} sm={6} md={4} key={item.title}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 2,
                    p: { xs: 2.5, sm: 3 },
                    background: "linear-gradient(135deg, #f6f8ff 0%, #eef3ff 100%)",
                    borderRadius: "16px",
                    border: "1px solid rgba(91, 124, 250, 0.15)",
                    transition: "all 0.3s ease",
                    animation: `${fadeUp} 0.5s ease-out ${0.08 * idx}s both`,
                    position: "relative",
                    zIndex: 1,
                    "&:hover": {
                      transform: "translateY(-6px)",
                      boxShadow: "0 16px 40px rgba(91, 124, 250, 0.15)",
                      border: "1px solid rgba(91, 124, 250, 0.3)",
                      zIndex: 2,
                      "& .benefit-icon": {
                        animation: `${bounce} 0.6s ease-in-out`,
                        background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                      },
                    },
                  }}
                >
                  <Box
                    className="benefit-icon"
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: "14px",
                      background: "linear-gradient(135deg, rgba(91,124,250,0.12) 0%, rgba(118,75,162,0.12) 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "26px",
                      flexShrink: 0,
                      transition: "all 0.3s ease",
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: { xs: "20px", sm: "22px" },
                        fontWeight: 700,
                        color: "#1f2937",
                        mb: 0.5,
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: "16px", sm: "17px" },
                        color: "#4b5563",
                        lineHeight: 1.6,
                      }}
                    >
                      {item.description}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Job Opportunities Section */}
      <JobOpportunities id="job-opportunities" />

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 8, sm: 10, md: 12 },
          background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Animated background elements */}
        <Box
          sx={{
            position: "absolute",
            top: "10%",
            left: "5%",
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.08)",
            animation: `${floatBall} 8s ease-in-out infinite`,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "15%",
            right: "8%",
            width: 180,
            height: 180,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.06)",
            animation: `${floatBall} 10s ease-in-out infinite 1s`,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            height: 400,
            borderRadius: "50%",
            border: "1px dashed rgba(255,255,255,0.15)",
            animation: `${keyframes`
              from { transform: translate(-50%, -50%) rotate(0deg); }
              to { transform: translate(-50%, -50%) rotate(360deg); }
            `} 40s linear infinite`,
          }}
        />

        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "26px", sm: "34px", md: "42px" },
                fontWeight: 800,
                color: "#fff",
                mb: 3,
                animation: `${fadeUp} 0.6s ease-out both`,
                textShadow: "0 2px 20px rgba(0,0,0,0.15)",
              }}
            >
              Ready to Join Our Team?
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "16px", sm: "19px", md: "20px" },
                color: "rgba(255,255,255,0.9)",
                lineHeight: 1.9,
                maxWidth: "700px",
                mx: "auto",
                animation: `${fadeUp} 0.6s ease-out 0.1s both`,
              }}
            >
              We're always looking for talented individuals to join our growing team. 
              Send us your resume and let's explore opportunities together.
            </Typography>

            <Box
              sx={{
                mt: 5,
                display: "flex",
                gap: 2,
                justifyContent: "center",
                flexWrap: "wrap",
                animation: `${fadeUp} 0.6s ease-out 0.2s both`,
              }}
            >
              <Button
                component={Link}
                to="/contact"
                sx={{
                  px: 4,
                  py: 1.5,
                  background: "#fff",
                  color: "#5b7cfa",
                  borderRadius: "50px",
                  fontSize: { xs: "16px", sm: "17px" },
                  fontWeight: 700,
                  textTransform: "none",
                  transition: "all 0.3s ease",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 12px 32px rgba(0,0,0,0.2)",
                    background: "#fff",
                  },
                }}
              >
                Let's Connect
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Careers;
